import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { MOMENTOS_POR_EVENTO } from '../constants/catalogoMomentos';
import type {
  EventoPesquisaPayload,
  ExibicaoPesquisaPayload,
  PesquisaSatisfacaoState,
} from '../types/pesquisaSatisfacaoTypes';
import { carregarContextoPesquisa, fecharPesquisa } from './actions';

const initialState: PesquisaSatisfacaoState = {
  momentoAtivoId: null,
  estado: 'PERGUNTA',
  nota: null,
  comentario: '',
  enviandoComentario: false,
  eventos: [],
  historico: [],
  timerPausado: false,
  correlacaoId: null,
  origemAtiva: null,
  papel: 'EDITOR',
  onboardingConcluidoEmIso: null,
  houveCriacao: false,
  contextoCarregado: false,
  carregamentoEmVoo: null,
  jaExibiuNestaSessao: false,
};

const pesquisaSatisfacaoSlice = createSlice({
  name: 'pesquisaSatisfacao',
  initialState,
  reducers: {
    eventoRegistrado: (state, action: PayloadAction<EventoPesquisaPayload>) => {
      const momento = MOMENTOS_POR_EVENTO[action.payload.evento];
      if (!momento) return;
      if (state.eventos.some((item) => item.momentoId === momento.id)) return;

      state.eventos.push({
        momentoId: momento.id,
        ocorridoEmIso: action.payload.ocorridoEmIso,
        recurso: action.payload.recurso,
      });
    },
    pesquisaExibida: (state, action: PayloadAction<ExibicaoPesquisaPayload>) => {
      state.momentoAtivoId = action.payload.momentoId;
      state.estado = 'PERGUNTA';
      state.nota = null;
      state.comentario = '';
      state.correlacaoId = action.payload.correlacaoId;
      state.origemAtiva = action.payload.origem;
      state.timerPausado = false;
      state.jaExibiuNestaSessao = true;
      state.historico.push({
        momentoId: action.payload.momentoId,
        exibidaEmIso: action.payload.exibidaEmIso,
        desfecho: 'EXIBIDA',
      });
      state.eventos = state.eventos.filter((item) => item.momentoId !== action.payload.momentoId);
    },
    /**
     * Recupera a marca de "já apareceu nesta sessão" que o `sessionStorage`
     * guardou. Sem ela um F5 valeria como sessão nova e a segunda pesquisa do dia
     * apareceria — o teto semanal seguraria depois, mas o incômodo do dia já teria
     * acontecido.
     */
    sessaoRestaurada: (state, action: PayloadAction<boolean>) => {
      state.jaExibiuNestaSessao = action.payload;
    },
    comentarioAlterado: (state, action: PayloadAction<string>) => {
      state.comentario = action.payload;
    },
    timerPausado: (state) => {
      state.timerPausado = true;
    },
    timerRetomado: (state) => {
      state.timerPausado = false;
    },
    pesquisaFechada: (state) => {
      state.momentoAtivoId = null;
      state.estado = 'PERGUNTA';
      state.nota = null;
      state.comentario = '';
      state.correlacaoId = null;
      state.origemAtiva = null;
      state.timerPausado = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(carregarContextoPesquisa.pending, (state, action) => {
        state.carregamentoEmVoo = action.meta.requestId;
        // Só descarta o que já estava carregado — numa RECARGA (troca de pessoa), o
        // histórico em memória é o de quem saiu e deixá-lo de pé com
        // `contextoCarregado: true` faria a régua decidir com o passado errado.
        // No primeiro carregamento não há o que limpar, e os `eventos` NUNCA saem:
        // eles são momentos que a pessoa acabou de viver, e é comum um deles chegar
        // antes de a resposta do contexto — apagá-los perderia a pesquisa.
        if (!state.contextoCarregado) return;
        state.contextoCarregado = false;
        state.historico = [];
      })
      .addCase(carregarContextoPesquisa.fulfilled, (state, action) => {
        if (state.carregamentoEmVoo !== action.meta.requestId) return;
        state.carregamentoEmVoo = null;
        // Os pendentes entram na MESMA fila dos eventos do front, então a régua
        // inteira (colisão, teto, prioridade) vale para eles sem exceção nenhuma.
        // Momento já consumido é descartado por `jaConsumido` no resolvedor.
        action.payload.eventosPendentes.forEach((pendente) => {
          if (state.eventos.some((item) => item.momentoId === pendente.pontoDeContatoId)) return;
          state.eventos.push({
            momentoId: pendente.pontoDeContatoId,
            ocorridoEmIso: pendente.ocorridoEmIso,
            recurso: null,
          });
        });
        state.historico = action.payload.historico;
        state.papel = action.payload.papel;
        state.onboardingConcluidoEmIso = action.payload.onboardingConcluidoEmIso;
        state.houveCriacao = action.payload.houveCriacao;
        state.contextoCarregado = true;
      })
      .addCase(carregarContextoPesquisa.rejected, (state, action) => {
        if (state.carregamentoEmVoo !== action.meta.requestId) return;
        state.carregamentoEmVoo = null;
        // `contextoCarregado` fica FALSE de propósito: sem histórico do servidor,
        // toda pesquisa pareceria inédita. Melhor não perguntar nada do que
        // perguntar a errada.
        state.contextoCarregado = false;
      })
      .addCase(fecharPesquisa.pending, (state, action) => {
        const { desfecho, nota, comentario } = action.meta.arg;
        state.historico = state.historico.map((item) =>
          item.momentoId === state.momentoAtivoId && item.desfecho === 'EXIBIDA'
            ? { ...item, desfecho }
            : item,
        );

        if (desfecho !== 'RESPONDIDA') return;

        // É aqui que a nota fica dada. O estado avança no `pending`, antes da
        // resposta do servidor: a promessa do design é que o segundo passo seja
        // sempre um extra, e esperar a rede para trocar de tela reintroduziria a
        // janela em que fechar o navegador perderia a resposta.
        if (comentario === null) {
          state.nota = nota;
          state.estado = 'COMENTARIO';
          return;
        }

        state.enviandoComentario = true;
      })
      .addCase(fecharPesquisa.fulfilled, (state, action) => {
        if (action.meta.arg.comentario === null) return;
        state.enviandoComentario = false;
        state.estado = 'ENVIADO';
      })
      .addCase(fecharPesquisa.rejected, (state, action) => {
        if (action.meta.arg.comentario === null) return;
        // O envio do comentário nunca vira erro na tela: a nota já está registrada
        // e o cartão segue para o agradecimento como se tivesse dado certo.
        state.enviandoComentario = false;
        state.estado = 'ENVIADO';
      });
  },
});

export const {
  eventoRegistrado,
  pesquisaExibida,
  sessaoRestaurada,
  comentarioAlterado,
  timerPausado,
  timerRetomado,
  pesquisaFechada,
} = pesquisaSatisfacaoSlice.actions;

export const pesquisaSatisfacaoReducer = pesquisaSatisfacaoSlice.reducer;
